import { useNavigate } from 'react-router-dom'
import { Alert, Button, EmptyState } from '@cinesend/ui-kit'
import { ArrowDown, ArrowUp, MinusCircle, PlusCircle } from '@phosphor-icons/react'
import DataTable from '../../components/data_table'
import RowMenu from '../../components/row_menu'
import AddModal from './add_modal'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '../../hooks/use_snackbar'
import { useConfirm } from '../../contexts/confirm_dialog'
import axios from 'axios'
import useTableFilters from '../../hooks/use_table_filters'
import { convertToLongDate } from '../../helpers/convert_date'
import useUtilities from '../../hooks/use_utilities'
import SectionItems from './section_items'

const ItemsTable = ({ category, invalidateKey, itemsKey, langID }) => {
  const { addMessage } = useSnackbar()
  const confirm = useConfirm()
  const { assetTypes } = useUtilities()
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedIDs, setSelectedIDs] = useState([])

  const mapTableToItemsKey = () => {
    if (itemsKey === 'category_items') {
      if (category.type === 'categories') {
        return 'child_categories'
      }

      return 'category_items'
    }

    return itemsKey
  }
  const internalItemsKey = mapTableToItemsKey()

  const isFeaturedContent = itemsKey === 'featured_content'
  const isSectionList = category.type === 'categories' && itemsKey === 'category_items'
  const isAdItemsList =
    itemsKey === 'category_items' && (category.type === 'warning-slates' || category.type === 'ad-pools')
  const isCategoryList = itemsKey === 'category_items' && !isSectionList && !isAdItemsList
  const isOrderedList = isSectionList || isCategoryList || isAdItemsList

  const navigate = useNavigate()

  let queryKey = `categories/${category.id}/get-addable-items?type=${['warning-slates', 'featured-content', 'ad-pools'].includes(showModal) ? 'assets' : showModal}`

  if ((showModal === 'warning-slates' || showModal === 'ad-pools') && itemsKey === 'category_items') {
    queryKey += `&adAssignment`
  }

  if (isFeaturedContent) {
    queryKey += `&featuredContentAssignment`
  }

  const itemsQueryKey = `categories/${category.id}/get-items?type=${itemsKey}`

  const sortingOptions = [
    { key: 'title', label: 'Name', direction: 'asc' },
    { key: 'start_date', label: 'Start Date', direction: 'asc' },
    { key: 'end_date', label: 'End Date', direction: 'asc' },
  ]

  const { searching, pagination, queryString } = useTableFilters({
    itemsQueryKey,
    sortingOptions,
    enableSearch: true,
    initialSort: category.enforce_az_order ? { key: 'title', direction: 'asc' } : {},
    queryExists: true,
  })

  const updateOrder = useMutation(
    ({ ids, startIndex }) => {
      return axios.put(`/categories/${category.id}/update-item-order`, {
        ids: ids,
        startIndex: startIndex,
        type: category.type,
        isFeaturedContent,
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryString)
        if (isOrderedList) queryClient.invalidateQueries(invalidateKey)
      },
      onError: (err) => {
        addMessage(err?.response?.data?.error ?? err.message, 'error')
      },
    }
  )

  const removeItems = useMutation(
    (ids) =>
      axios.delete(`/categories/${category.id}/remove-items`, {
        data: { ids: ids?.length ? ids : selectedIDs, type: category.type, isFeaturedContent },
      }),
    {
      onSuccess: () => {
        setSelectedIDs([])
        queryClient.invalidateQueries(queryString)
        if (isOrderedList) queryClient.invalidateQueries(invalidateKey)
      },
      onError: (err) => {
        addMessage(err?.response?.data?.error ?? err.message, 'error')
      },
    }
  )

  const {
    data,
    isLoading,
    isRefetching,
    isError,
    error: errors,
    refetch,
  } = useQuery([itemsQueryKey, queryString], {
    enabled: !isOrderedList,
  })
  const categoryItems = isSectionList
    ? category.child_categories || []
    : isCategoryList
      ? category.assigned_items || []
      : isAdItemsList
        ? category.category_items || []
        : data?.data || []

  const canReorder = !category.enforce_az_order && searching.search === ''

  const getItemName = (data) => data?.category_values?.find((v) => v.language_id === langID)?.value || data.title

  const moveItem = (index, offset) => {
    const order = [...categoryItems]
    order.splice(index + offset, 0, order.splice(index, 1)[0])
    const startIndex = (pagination.currentPage ?? 0) * pagination.rowsPerPage + 1
    updateOrder.mutate({ ids: order.map((item) => item.id), startIndex })
  }

  const removeSectionItem = async (item) => {
    const name = getItemName(item)
    const accepted = await confirm({
      title: 'Remove category?',
      message: `Removes “${name}” from this section. The category itself stays in the catalog.`,
      confirmText: 'Remove',
      confirmVariant: 'destructive',
    })
    if (accepted) removeItems.mutate([item.id])
  }

  const removeCategoryItem = async (item) => {
    const name = getItemName(item)
    const accepted = await confirm({
      title: 'Remove item?',
      message: `Removes “${name}” from this category. The asset stays in the cycle.`,
      confirmText: 'Remove',
      confirmVariant: 'destructive',
    })
    if (accepted) removeItems.mutate([item.id])
  }

  const listedItems = ({ modalTitle, ...props }) => (
    <>
      {category.issues.length > 0 && (
        <Alert tone='error' size='sm' className='mb-2'>
          {category.issues[0]?.description}
        </Alert>
      )}
      <SectionItems
        items={categoryItems}
        isLoading={isLoading}
        isFetching={isRefetching}
        error={isError ? errors : null}
        onRetry={refetch}
        getItemName={getItemName}
        canReorder={canReorder}
        onMove={moveItem}
        pagination={pagination}
        totalRows={data?.meta.total || 0}
        {...props}
      />
      {showModal && (
        <AddModal
          category={category}
          invalidateKey={isOrderedList ? invalidateKey : itemsQueryKey}
          onClose={() => setShowModal(false)}
          queryKey={[queryKey]}
          type={showModal}
          title={modalTitle}
        />
      )}
    </>
  )

  if (category.type === 'categories' && itemsKey === 'category_items') {
    return listedItems({
      itemPath: (item) => `/airlines/${category.airline_id}/cycles/${category.cycle_id}/categories/${item.id}/details`,
      onRemove: removeSectionItem,
      onAdd: () => setShowModal('categories'),
      isLoading: false,
      isFetching: false,
      error: null,
      pagination: null,
      totalRows: 0,
    })
  }

  if (isCategoryList) {
    return listedItems({
      title: 'Items',
      description: 'Ordered items in this category.',
      emptyText: 'No items in this category yet.',
      addLabel: 'Add item',
      removeLabel: (name) => `Remove ${name} from category`,
      modalTitle: 'Add item',
      subtitle: (item, dates) => {
        const type = assetTypes?.find((entry) => entry.value === item.asset_type)
        return [type?.label, dates].filter(Boolean).join(' · ')
      },
      itemPath: (item) => {
        const type = assetTypes?.find((entry) => entry.value === item.asset_type)
        if (!type?.route_value) return null
        return `/airlines/${category.airline_id}/cycles/${category.cycle_id}/metadata/${type.route_value}/${item.id}`
      },
      onRemove: removeCategoryItem,
      onAdd: () => setShowModal('assets'),
      isLoading: false,
      isFetching: false,
      error: null,
      pagination: null,
      totalRows: 0,
    })
  }

  if (isAdItemsList) {
    const isAdPool = category.type === 'ad-pools'
    return listedItems({
      title: 'Items',
      description: isAdPool ? 'Ordered ads in this ad pool.' : 'Ordered ads in this slate group.',
      emptyText: isAdPool ? 'No ads in this ad pool yet.' : 'No ads in this slate group yet.',
      addLabel: isAdPool ? 'Add preroll ad' : 'Add warning slate',
      removeLabel: (name) => `Remove ${name} from ${isAdPool ? 'ad pool' : 'slate group'}`,
      modalTitle: isAdPool ? 'Add preroll ad' : 'Add warning slate',
      subtitle: (_item, dates) => dates || null,
      itemPath: (item) => `/airlines/${category.airline_id}/cycles/${category.cycle_id}/ads/clips/${item.id}`,
      onRemove: removeCategoryItem,
      onAdd: () => setShowModal(isAdPool ? 'ad-pools' : 'warning-slates'),
      isLoading: false,
      isFetching: false,
      error: null,
      pagination: null,
      totalRows: 0,
    })
  }

  const columns = [
    { key: 'title', header: 'Name', render: getItemName },
    {
      key: 'start_date',
      header: 'Start Date',
      render: (data) => convertToLongDate(data.start_date),
    },
    {
      key: 'end_date',
      header: 'End Date',
      render: (data) => convertToLongDate(data.end_date),
    },
    ...(canReorder
      ? [
          {
            key: 'actions',
            header: '',
            width: '100px',
            render: (data) => {
              const index = categoryItems.indexOf(data)

              return (
                <RowMenu
                  label={`Actions for ${getItemName(data)}`}
                  items={[
                    {
                      id: 'move-up',
                      label: 'Move up',
                      icon: <ArrowUp size={20} />,
                      disabled: index === 0,
                      onSelect: () => moveItem(index, -1),
                    },
                    {
                      id: 'move-down',
                      label: 'Move down',
                      icon: <ArrowDown size={20} />,
                      disabled: index === categoryItems.length - 1,
                      onSelect: () => moveItem(index, 1),
                    },
                  ]}
                />
              )
            },
          },
        ]
      : []),
  ]

  return (
    <>
      {category.issues.length > 0 && (
        <Alert tone='error' size='sm' className='mb-2'>
          {category.issues[0]?.description}
        </Alert>
      )}
      <DataTable
        caption='Category items'
        columns={columns}
        rows={categoryItems}
        onRowClick={
          category.type === 'categories'
            ? (data) => {
                if (isFeaturedContent) {
                  return navigate(
                    `/airlines/${category.airline_id}/cycles/${category.cycle_id}/metadata/featured-content/${data.id}`
                  )
                }

                return navigate(
                  `/airlines/${category.airline_id}/cycles/${category.cycle_id}/categories/${data.id}/details`
                )
              }
            : undefined
        }
        isLoading={isLoading || isRefetching}
        error={isError ? errors : null}
        onRetry={refetch}
        empty={<EmptyState title='No items found!' detail='Add items to begin.' />}
        searching={searching}
        actions={
          <>
            {(!category.type || category.type === 'assets') && (
              <Button leadingIcon={<PlusCircle size={20} />} onClick={() => setShowModal('assets')}>
                Add Assets
              </Button>
            )}
            {(!category.type || (category.type === 'categories' && itemsKey !== 'featured_content')) && (
              <Button leadingIcon={<PlusCircle size={20} />} onClick={() => setShowModal('categories')}>
                Add Categories
              </Button>
            )}
            {(!category.type || category.type === 'warning-slates') && (
              <Button leadingIcon={<PlusCircle size={20} />} onClick={() => setShowModal('warning-slates')}>
                {itemsKey === 'category_items' ? 'Add Warning Slates' : 'Add Assets'}
              </Button>
            )}
            {(!category.type || category.type === 'ad-pools') && (
              <Button leadingIcon={<PlusCircle size={20} />} onClick={() => setShowModal('ad-pools')}>
                {itemsKey === 'category_items' ? 'Add Preroll Ads' : 'Add Assets'}
              </Button>
            )}
            {(!category.type || (category.type === 'categories' && itemsKey === 'featured_content')) && (
              <>
                <Button leadingIcon={<PlusCircle size={20} />} onClick={() => setShowModal('assets')}>
                  Featured Asset
                </Button>
                <Button leadingIcon={<PlusCircle size={20} />} onClick={() => setShowModal('categories')}>
                  Featured Category
                </Button>
              </>
            )}
            {selectedIDs.length > 0 && (
              <Button
                variant='destructive'
                leadingIcon={<MinusCircle size={20} />}
                onClick={() => removeItems.mutate()}
              >
                Remove ({selectedIDs.length}) Items
              </Button>
            )}
          </>
        }
        pagination={pagination}
        totalRows={data?.meta.total || 0}
        selectedKeys={selectedIDs.map(String)}
        onSelectionChange={(keys) =>
          setSelectedIDs(categoryItems.filter(({ id }) => keys.includes(String(id))).map(({ id }) => id))
        }
        selectionLabel={(data) => `Select ${getItemName(data)}`}
      />
      {showModal && (
        <AddModal
          category={category}
          invalidateKey={itemsQueryKey}
          onClose={() => setShowModal(false)}
          queryKey={[queryKey]}
          type={showModal}
        />
      )}
    </>
  )
}

export default ItemsTable
